import { ethers } from "ethers";
import { BASE_DEPLOYMENT_CHAIN_ID, EVM_CONFIG } from "../config";
import { RpcReadQueue } from "./rpc-read-queue";

const READ_METHODS = new Set([
  "eth_chainId",
  "eth_blockNumber",
  "eth_getBlockByNumber",
  "eth_getBlockByHash",
  "eth_getLogs",
  "eth_getTransactionByHash",
  "eth_getTransactionReceipt",
  "eth_getBalance",
  "eth_getCode",
  "eth_getStorageAt",
  "eth_getTransactionCount",
  "eth_call",
  "eth_estimateGas",
  "eth_gasPrice",
  "eth_maxPriorityFeePerGas",
  "eth_feeHistory",
]);

const MAX_RATE_LIMIT_RETRIES = 2;

/** Only explicit log range/result limits justify splitting a historical read. */
function logRangeLimit(error: unknown): { maximum?: bigint } | undefined {
  const rpcError = error as any;
  const message = [
    rpcError?.message,
    rpcError?.error?.message,
    rpcError?.info?.error?.message,
  ]
    .filter((value) => typeof value === "string")
    .join(" ");
  const explicit = /eth_getLogs is limited to a (\d+) range/i.exec(message);
  if (explicit && BigInt(explicit[1]) > 0n)
    return { maximum: BigInt(explicit[1]) };
  if (
    /(?:block range|query range).*(?:too (?:large|wide)|exceed|limit|maximum)|(?:exceed|maximum|limit).*block range|query returned more than|too many (?:logs|results)|(?:log response|response size).*(?:exceed|too large|limit)/i.test(
      message,
    )
  )
    return {};
  return undefined;
}

export function rpcRetryDelay(
  value: string | undefined,
  now = Date.now(),
): number {
  if (value && /^\d+$/.test(value.trim())) return Number(value) * 1_000;
  const date = value ? Date.parse(value) : NaN;
  return Number.isFinite(date) ? Math.max(0, date - now) : 60_000;
}

/** Public-chain reads must not depend on an injected wallet's RPC limits. */
class BaseReadProvider extends ethers.JsonRpcProvider {
  private maximumLogRange?: bigint;
  private readonly queue = new RpcReadQueue({
    concurrent: 4,
    intervalMs: 0,
    requestsPerWindow: 180,
    windowMs: 60_000,
    interactiveReserve: 40,
  });
  private readonly activeRequests = new Set<ethers.FetchRequest>();
  private readonly inFlight = new Map<
    string,
    Promise<ethers.JsonRpcResult | ethers.JsonRpcError>
  >();

  override async _send(
    payload: ethers.JsonRpcPayload | Array<ethers.JsonRpcPayload>,
  ): ReturnType<ethers.JsonRpcProvider["_send"]> {
    const calls = Array.isArray(payload) ? payload : [payload];
    if (calls.some((call) => !READ_METHODS.has(call.method)))
      throw new Error("The app RPC provider only permits public-chain reads");
    if (calls.length !== 1)
      throw new Error("App RPC reads must be individual requests");
    const call = calls[0];
    const args = call.params as any[];
    const shortLogRange =
      call.method === "eth_getLogs" &&
      args?.[0]?.fromBlock &&
      args?.[0]?.toBlock &&
      /^0x[0-9a-f]+$/i.test(args[0].fromBlock) &&
      /^0x[0-9a-f]+$/i.test(args[0].toBlock) &&
      BigInt(args[0].toBlock) >= BigInt(args[0].fromBlock) &&
      BigInt(args[0].toBlock) - BigInt(args[0].fromBlock) <= 1024n;
    const priority =
      ["eth_chainId", "eth_blockNumber", "eth_getBlockByNumber"].includes(
        call.method,
      ) ||
      shortLogRange ||
      (call.method === "eth_call" &&
        args?.[0]?.to?.toLowerCase() ===
          "0xca11bde05977b3631167028862be2a173976ca11")
        ? "interactive"
        : "normal";
    // Canonical block-number reads are before/after reorg barriers. A later
    // barrier must not join a request another feature started before its work.
    const key = JSON.stringify([
      call.method,
      call.params,
      call.method === "eth_getBlockByNumber" ? call.id : null,
    ]);
    // Share only simultaneous identical reads. Never retain a completed block,
    // chain, receipt or nullifier result across a later validation step.
    let operation = this.inFlight.get(key);
    if (!operation) {
      operation = (async () => {
        for (let attempt = 0; ; attempt++) {
          const queuedAt = performance.now();
          // Background history scans queue many reads behind interactive ones;
          // a read gives up only after waiting well beyond one provider cooldown.
          const queueDeadline = AbortSignal.timeout(45_000);
          const response = await this.queue.run(
            async () => {
              const queueMs = Math.round(performance.now() - queuedAt);
              if (queueMs > 250)
                console.debug("[wallet-rpc]", { method: call.method, queueMs });
              if (this.destroyed)
                throw new Error("App RPC provider was closed");
              const request = this._getConnection();
              request.body = JSON.stringify(call);
              request.setHeader("content-type", "application/json");
              // ethers 6.16 treats Retry-After seconds as milliseconds. Handle 429
              // outside FetchRequest so the cooldown has its own timeout budget.
              request.retryFunc = async () => false;
              this.activeRequests.add(request);
              try {
                const response = await request.send();
                if (response.statusCode === 429) {
                  const delay = rpcRetryDelay(response.headers["retry-after"]);
                  // Cool down all reads, including requests queued by other features.
                  this.queue.cooldown(
                    Number.isFinite(delay) ? delay : Infinity,
                  );
                }
                return response;
              } finally {
                this.activeRequests.delete(request);
              }
            },
            { priority, signal: queueDeadline },
          );
          if (response.statusCode === 429) {
            const delay = rpcRetryDelay(response.headers["retry-after"]);
            if (
              attempt >= MAX_RATE_LIMIT_RETRIES ||
              !Number.isFinite(delay) ||
              delay > 120_000
            )
              throw new Error(
                "Chain reads are rate limited. Wait for the provider cooldown, then retry.",
              );
            continue;
          }
          // Public RPCs can report a JSON-RPC range limit using HTTP 413.
          // Preserve that specific error so send() can split the exact range;
          // unrelated HTTP failures still fail normally.
          if (response.statusCode === 413 && call.method === "eth_getLogs") {
            let limited;
            try {
              limited = response.bodyJson;
            } catch {
              // A non-JSON HTTP error is not a proven log-range limitation.
            }
            if (limited?.id === call.id && logRangeLimit(limited))
              return limited as ethers.JsonRpcError;
          }
          response.assertOk();
          const result = response.bodyJson;
          const value = Array.isArray(result) ? result[0] : result;
          if (
            !value ||
            value.id !== call.id ||
            (!("result" in value) && !("error" in value))
          )
            throw new Error("App RPC returned an invalid response");
          return value as ethers.JsonRpcResult | ethers.JsonRpcError;
        }
      })();
      this.inFlight.set(key, operation);
      const clear = () => {
        if (this.inFlight.get(key) === operation) this.inFlight.delete(key);
      };
      void operation.then(clear, clear);
    }
    // Each ethers caller has its own request ID even when transport is shared.
    // ethers' concrete provider narrows this wire type to success results;
    // keep error envelopes intact so its normal getRpcError path rejects them.
    return [{ ...(await operation), id: call.id }] as Awaited<
      ReturnType<ethers.JsonRpcProvider["_send"]>
    >;
  }

  override destroy(): void {
    super.destroy();
    this.queue.close();
    for (const request of this.activeRequests) request.cancel();
  }

  override async send(
    method: string,
    params: any[] | Record<string, any>,
  ): Promise<any> {
    if (!READ_METHODS.has(method))
      throw new Error("The app RPC provider only permits public-chain reads");
    const filter =
      Array.isArray(params) && params.length === 1 ? params[0] : undefined;
    if (
      method === "eth_getLogs" &&
      filter &&
      !filter.blockHash &&
      typeof filter.fromBlock === "string" &&
      typeof filter.toBlock === "string" &&
      /^0x[0-9a-f]+$/i.test(filter.fromBlock) &&
      /^0x[0-9a-f]+$/i.test(filter.toBlock) &&
      BigInt(filter.toBlock) >= BigInt(filter.fromBlock)
    )
      return this.readLogRange(filter);
    return super.send(method, params);
  }

  private async readLogRange(filter: Record<string, unknown>): Promise<any[]> {
    const from = BigInt(filter.fromBlock as string);
    const to = BigInt(filter.toBlock as string);
    const width = to - from + 1n;
    if (this.maximumLogRange && width > this.maximumLogRange) {
      const logs: any[] = [];
      for (let start = from; start <= to; ) {
        const end = start + this.maximumLogRange - 1n;
        const last = end < to ? end : to;
        logs.push(
          ...(await this.readLogRange({
            ...filter,
            fromBlock: ethers.toQuantity(start),
            toBlock: ethers.toQuantity(last),
          })),
        );
        start = last + 1n;
      }
      return logs;
    }
    try {
      const logs = await super.send("eth_getLogs", [filter]);
      if (!Array.isArray(logs))
        throw new Error("App RPC returned invalid logs");
      return logs;
    } catch (error) {
      const limit = logRangeLimit(error);
      if (!limit || width <= 1n) throw error;
      // A result-count limit may still fail for one block; that error must
      // propagate, never turn into an empty or partially recovered history.
      const smaller =
        limit.maximum && limit.maximum < width
          ? limit.maximum
          : (width + 1n) / 2n;
      this.maximumLogRange =
        this.maximumLogRange && this.maximumLogRange < smaller
          ? this.maximumLogRange
          : smaller;
      return this.readLogRange(filter);
    }
  }
}

export function createBaseReadProvider(
  url: string,
  chainId: number,
): ethers.JsonRpcProvider {
  if (!Number.isSafeInteger(chainId) || chainId <= 0)
    throw new Error("Invalid Base RPC chain");
  const request = new ethers.FetchRequest(url);
  request.timeout = 15_000;
  // Explicit network WITHOUT staticNetwork keeps actual RPC chain checks active.
  // Single requests also work with RPC proxies that do not accept JSON batches.
  return new BaseReadProvider(request, chainId, {
    staticNetwork: false,
    batchMaxCount: 1,
    // A 250ms response cache would conceal a reorg between fast before/after
    // hash checks. In-flight coalescing above is the only read reuse here.
    cacheTimeout: -1,
  });
}

/** The Base deployment's RPC proxy, served on every network's page. */
const BASE_RPC_PROXY_PATH = "/api/proxy/evm-rpc";

/** The same-origin JSON-RPC proxies: Base and DogeOS Chikyu. */
const EVM_RPC_PROXY_PATHS: ReadonlySet<string> = new Set([
  BASE_RPC_PROXY_PATH,
  "/api/proxy/dogeos-rpc",
]);

let shared: { key: string; provider: ethers.JsonRpcProvider } | undefined;
let registryShared:
  | { key: string; provider: ethers.JsonRpcProvider }
  | undefined;

function sameOriginProxyUrl(path: string): URL {
  if (typeof window === "undefined")
    throw new Error("The app RPC requires a browser origin");
  const url = new URL(path, window.location.origin);
  if (
    url.origin !== window.location.origin ||
    !EVM_RPC_PROXY_PATHS.has(url.pathname)
  ) {
    throw new Error("Base RPC must use the configured same-origin proxy");
  }
  return url;
}

export function getBaseReadProvider(): ethers.JsonRpcProvider {
  const url = sameOriginProxyUrl(EVM_CONFIG.BASE_RPC_URL);
  const key = `${url.href}:${EVM_CONFIG.CHAIN_ID}`;
  if (!shared || shared.key !== key) {
    shared?.provider.destroy();
    shared = {
      key,
      provider: createBaseReadProvider(url.href, EVM_CONFIG.CHAIN_ID),
    };
  }
  return shared.provider;
}

/**
 * Reads of the handle registry, which lives on the Base deployment's chain
 * whichever network this page serves (DogeOS reads it too).
 */
export function getHandleRegistryReadProvider(): ethers.JsonRpcProvider {
  const url = sameOriginProxyUrl(BASE_RPC_PROXY_PATH);
  if (
    EVM_CONFIG.CHAIN_ID === BASE_DEPLOYMENT_CHAIN_ID &&
    sameOriginProxyUrl(EVM_CONFIG.BASE_RPC_URL).href === url.href
  )
    return getBaseReadProvider();
  const key = `${url.href}:${BASE_DEPLOYMENT_CHAIN_ID}`;
  if (!registryShared || registryShared.key !== key) {
    registryShared?.provider.destroy();
    registryShared = {
      key,
      provider: createBaseReadProvider(url.href, BASE_DEPLOYMENT_CHAIN_ID),
    };
  }
  return registryShared.provider;
}
